import { AccordionState } from '@app/enums';
import { logger } from '@app/logger';
import { OfferType } from '@core/models/offer/enums';
import OfferEvent from '@core/models/offer/OfferEvent';
import { BetBuilderMarket } from '@sportOfferFederation/bet-builder/types/BetBuilderMarket';
import { BetBuilderOdd } from '@sportOfferFederation/bet-builder/types/BetBuilderOdd';
import { getAnalyticsEventId } from '@sportOfferFederation/bet-builder/utils/getAnalyticsEventId';
import { Analytics as AnalyticsSDK } from '@superbet-group/web.analytics.sdk';

import { AddToBetslipPayload } from '../types/AddToBetslipPayload';
import { AddToBetslipResult } from '../types/AddToBetslipResult';
import { BetBuilderMarketCategory } from '../types/BetBuilderMarketCategory';
import { MultiEventBetBuilderBannerLocation } from '../enums/MultiEventBetBuilderBannerLocation';

const OfferStatus = {
    Live: 'live',
    Prematch: 'prematch',
} as const;
type OfferStatus = typeof OfferStatus[keyof typeof OfferStatus];

type BannerPayload = {
    competitionsCount: number;
    eventsCount: number;
    location: MultiEventBetBuilderBannerLocation;
};

type ScreenPayload = {
    eventIds: OfferEvent['id'][];
};

type EventCardPayload = {
    eventId: OfferEvent['id'];
    eventPosition: number;
};

type EventCardHeaderClickedPayload = EventCardPayload & {
    newState: AccordionState;
};

type MarketCategoryClickedPayload = EventCardPayload & {
    marketCategoryName: BetBuilderMarketCategory['name'];
    availableMarketIds: BetBuilderMarket['id'][];
};

type MarketFilterClickedPayload = MarketCategoryClickedPayload & {
    marketId: BetBuilderMarket['id'];
    marketPosition: number;
};

type LegPayload = MarketFilterClickedPayload & {
    oddId: BetBuilderOdd['uuid'];
    oddValue: BetBuilderOdd['price'];
};

type EventWithOdds = {
    eventId: OfferEvent['id'];
    oddUuids: BetBuilderOdd['uuid'][];
};

type SummaryInteractionPayload = {
    eventWithOddsList: EventWithOdds[];
    newState: AccordionState;
    oddValue: number;
};

type SummarySelectionClickedPayload = SummaryInteractionPayload & {
    eventId: OfferEvent['id'];
};

type EventWithOddsAndStatus = EventWithOdds & {
    status: OfferType;
};

type SummaryClearedPayload = {
    eventWithOddsAndStatusList: EventWithOddsAndStatus[];
    oddValue: number;
};

type SummaryUnavailableRemovedPayload = {
    eventWithOddsAndStatusList: EventWithOddsAndStatus[];
};

type SummarySubmitClickedPayload = {
    addToBetslipPayloads: AddToBetslipPayload[];
    oddValue: number;
};

type SummarySubmitResultPayload = {
    addToBetslipResults: AddToBetslipResult[];
};

interface MEBBAnalyticsService {
    bannerDisplayed(payload: BannerPayload): void;
    bannerClicked(payload: BannerPayload): void;
    screenLoaded(payload: ScreenPayload): void;
    screenBackClicked(payload: ScreenPayload): void;
    eventCardHeaderClicked(payload: EventCardHeaderClickedPayload): void;
    marketCategoryClicked(payload: MarketCategoryClickedPayload): void;
    marketFilterClicked(payload: MarketFilterClickedPayload): void;
    legAdded(payload: LegPayload): void;
    legRemoved(payload: LegPayload): void;
    summaryInteraction(payload: SummaryInteractionPayload): void;
    summaryEventHeaderClicked(payload: SummarySelectionClickedPayload): void;
    summaryCleared(payload: SummaryClearedPayload): void;
    summaryUnavailableRemoved(payload: SummaryUnavailableRemovedPayload): void;
    summarySubmitClicked(payload: SummarySubmitClickedPayload): void;
    summarySubmitResult(payload: SummarySubmitResultPayload): void;
}

let mebbAnalyticsService: MEBBAnalyticsService | null = null;

export function useMEBBAnalytics(): MEBBAnalyticsService {
    if (!mebbAnalyticsService) {
        mebbAnalyticsService = createMEBBAnalyticsService();
    }

    return mebbAnalyticsService;
}

const createMEBBAnalyticsService = (): MEBBAnalyticsService => {
    const bannerDisplayed = (payload: BannerPayload): void => {
        try {
            AnalyticsSDK.getInstance().log('MEBB_Banner_Display', {
                competition_count: payload.competitionsCount,
                match_count: payload.eventsCount,
                Location: payload.location,
            });
        } catch (e) {
            (e as Error).message += 'Resulted in not calling "MEBB_Banner_Display"';
            logger.error(e as Error);
        }
    };

    const bannerClicked = (payload: BannerPayload): void => {
        try {
            AnalyticsSDK.getInstance().log('MEBB_Banner_Click', {
                competition_count: payload.competitionsCount,
                match_count: payload.eventsCount,
                Location: payload.location,
            });
        } catch (e) {
            (e as Error).message += 'Resulted in not calling "MEBB_Banner_Click"';
            logger.error(e as Error);
        }
    };

    const screenLoaded = (payload: ScreenPayload): void => {
        try {
            AnalyticsSDK.getInstance().log('MEBB_Screen_Loaded', {
                match_count: payload.eventIds.length,
                match_id_list: JSON.stringify(payload.eventIds.map((eventId) => getAnalyticsEventId(eventId))),
            });
        } catch (e) {
            (e as Error).message += 'Resulted in not calling "MEBB_Screen_Loaded"';
            logger.error(e as Error);
        }
    };

    const screenBackClicked = (payload: ScreenPayload): void => {
        try {
            AnalyticsSDK.getInstance().log('MEBB_Screen_Back_Click', {
                match_count: payload.eventIds.length,
                match_id_list: JSON.stringify(payload.eventIds.map((eventId) => getAnalyticsEventId(eventId))),
            });
        } catch (e) {
            (e as Error).message += 'Resulted in not calling "MEBB_Screen_Back_Click"';
            logger.error(e as Error);
        }
    };

    const eventCardHeaderClicked = (payload: EventCardHeaderClickedPayload): void => {
        try {
            AnalyticsSDK.getInstance().log('MEBB_Event_Header_Click', {
                match_id: getAnalyticsEventId(payload.eventId),
                position: payload.eventPosition,
                new_state: payload.newState,
            });
        } catch (e) {
            (e as Error).message += 'Resulted in not calling "MEBB_Event_Header_Click"';
            logger.error(e as Error);
        }
    };

    const marketCategoryClicked = (payload: MarketCategoryClickedPayload): void => {
        try {
            AnalyticsSDK.getInstance().log('MEBB_Market_Category_Click', {
                match_id: getAnalyticsEventId(payload.eventId),
                position: payload.eventPosition,
                market_category: payload.marketCategoryName,
                available_market_id_list: JSON.stringify(payload.availableMarketIds),
            });
        } catch (e) {
            (e as Error).message += 'Resulted in not calling "MEBB_Market_Category_Click"';
            logger.error(e as Error);
        }
    };

    const marketFilterClicked = (payload: MarketFilterClickedPayload): void => {
        try {
            AnalyticsSDK.getInstance().log('MEBB_Market_Click', {
                match_id: getAnalyticsEventId(payload.eventId),
                position: payload.eventPosition,
                market_category: payload.marketCategoryName,
                available_market_id_list: JSON.stringify(payload.availableMarketIds),
                MarketId: payload.marketId,
                market_position: payload.marketPosition,
            });
        } catch (e) {
            (e as Error).message += 'Resulted in not calling "MEBB_Market_Click"';
            logger.error(e as Error);
        }
    };

    const legAdded = (payload: LegPayload): void => {
        try {
            AnalyticsSDK.getInstance().log('MEBB_Leg_Add', {
                match_id: getAnalyticsEventId(payload.eventId),
                position: payload.eventPosition,
                market_category: payload.marketCategoryName,
                available_market_id_list: JSON.stringify(payload.availableMarketIds),
                MarketId: payload.marketId,
                market_position: payload.marketPosition,
                odd_id: payload.oddId,
                Odd: String(payload.oddValue),
            });
        } catch (e) {
            (e as Error).message += 'Resulted in not calling "MEBB_Leg_Add"';
            logger.error(e as Error);
        }
    };

    const legRemoved = (payload: LegPayload): void => {
        try {
            AnalyticsSDK.getInstance().log('MEBB_Leg_Remove', {
                match_id: getAnalyticsEventId(payload.eventId),
                position: payload.eventPosition,
                market_category: payload.marketCategoryName,
                available_market_id_list: JSON.stringify(payload.availableMarketIds),
                MarketId: payload.marketId,
                market_position: payload.marketPosition,
                odd_id: payload.oddId,
                Odd: String(payload.oddValue),
            });
        } catch (e) {
            (e as Error).message += 'Resulted in not calling "MEBB_Leg_Remove"';
            logger.error(e as Error);
        }
    };

    const summaryInteraction = (payload: SummaryInteractionPayload): void => {
        try {
            const eventWithOddsList = payload.eventWithOddsList.map(({ eventId, oddUuids }) => ({ match_id: getAnalyticsEventId(eventId), odd_id_list: oddUuids }));

            AnalyticsSDK.getInstance().log('MEBB_Summary', {
                match_count: eventWithOddsList.length,
                match_with_odds_list: JSON.stringify(eventWithOddsList),
                new_state: payload.newState,
                Odd: String(payload.oddValue),
            });
        } catch (e) {
            (e as Error).message += 'Resulted in not calling "MEBB_Summary"';
            logger.error(e as Error);
        }
    };

    const summaryEventHeaderClicked = (payload: SummarySelectionClickedPayload): void => {
        try {
            const eventWithOddsList = payload.eventWithOddsList.map(({ eventId, oddUuids }) => ({ match_id: getAnalyticsEventId(eventId), odd_id_list: oddUuids }));

            AnalyticsSDK.getInstance().log('MEBB_Summary_Event_Header_Click', {
                match_count: eventWithOddsList.length,
                match_id: getAnalyticsEventId(payload.eventId),
                match_with_odds_list: JSON.stringify(eventWithOddsList),
                new_state: payload.newState,
                Odd: String(payload.oddValue),
            });
        } catch (e) {
            (e as Error).message += 'Resulted in not calling "MEBB_Summary_Event_Header_Click"';
            logger.error(e as Error);
        }
    };

    const summaryCleared = (payload: SummaryClearedPayload): void => {
        try {
            // eslint-disable-next-line camelcase
            const eventWithOddsList: { match_id: string; odd_id_list: any; }[] = [];
            // eslint-disable-next-line camelcase
            const eventWithStatusList: { match_id: string; status: OfferStatus }[] = [];

            payload.eventWithOddsAndStatusList.forEach(({ eventId, oddUuids, status }) => {
                eventWithOddsList.push({
                    match_id: getAnalyticsEventId(eventId),
                    odd_id_list: oddUuids,
                });

                eventWithStatusList.push({
                    match_id: getAnalyticsEventId(eventId),
                    status: status === OfferType.live ? OfferStatus.Live : OfferStatus.Prematch,
                });
            });

            AnalyticsSDK.getInstance().log('MEBB_Summary_Clear', {
                match_count: payload.eventWithOddsAndStatusList.length,
                match_with_odds_list: JSON.stringify(eventWithOddsList),
                match_with_status_list: JSON.stringify(eventWithStatusList),
                Odd: String(payload.oddValue),
            });
        } catch (e) {
            (e as Error).message += 'Resulted in not calling "MEBB_Summary_Clear"';
            logger.error(e as Error);
        }
    };

    const summaryUnavailableRemoved = (payload: SummaryUnavailableRemovedPayload): void => {
        try {
            // eslint-disable-next-line camelcase
            const eventWithOddsList: { match_id: string; odd_id_list: any; }[] = [];
            // eslint-disable-next-line camelcase
            const eventWithStatusList: { match_id: string; status: OfferType; }[] = [];

            payload.eventWithOddsAndStatusList.forEach(({ eventId, oddUuids, status }) => {
                eventWithOddsList.push({
                    match_id: getAnalyticsEventId(eventId),
                    odd_id_list: oddUuids,
                });

                eventWithStatusList.push({
                    match_id: getAnalyticsEventId(eventId),
                    status,
                });
            });

            AnalyticsSDK.getInstance().log('MEBB_Unavailable_Remove', {
                match_count: payload.eventWithOddsAndStatusList.length,
                match_with_odds_list: JSON.stringify(eventWithOddsList),
                match_with_status_list: JSON.stringify(eventWithStatusList),
            });
        } catch (e) {
            (e as Error).message += 'Resulted in not calling "MEBB_Summary_Unavailable_Remove"';
            logger.error(e as Error);
        }
    };

    const summarySubmitClicked = (payload: SummarySubmitClickedPayload): void => {
        try {
            const eventWithOddsList = payload.addToBetslipPayloads.map(
                ({ eventId, betslipOdd }) => ({
                    match_id: getAnalyticsEventId(eventId),
                    bb_odd_id: betslipOdd?.id ?? null,
                }),
            );

            AnalyticsSDK.getInstance().log('MEBB_Betslip_Event_Add', {
                match_with_bb_odd_id_list: JSON.stringify(eventWithOddsList),
                match_count: payload.addToBetslipPayloads.length,
                Odd: String(payload.oddValue),
            });
        } catch (e) {
            (e as Error).message += 'Resulted in not calling "MEBB_Betslip_Event_Add"';
            logger.error(e as Error);
        }
    };

    const summarySubmitResult = (payload: SummarySubmitResultPayload): void => {
        try {
            // eslint-disable-next-line camelcase
            const successList: { match_id: string; bb_odd_id: string | null; }[] = [];
            // eslint-disable-next-line camelcase
            const failureList: { match_id: string; bb_odd_id: string | null; }[] = [];

            payload.addToBetslipResults.forEach(({ eventId, betslipOdd, success }) => {
                const result = { match_id: getAnalyticsEventId(eventId), bb_odd_id: betslipOdd ? String(betslipOdd.id) : null };

                if (success) {
                    successList.push(result);
                } else {
                    failureList.push(result);
                }
            });

            AnalyticsSDK.getInstance().log('MEBB_Betslip_Event_Add_Result', {
                failure_match_with_bb_odd_id_list: JSON.stringify(failureList),
                failure_match_count: failureList.length,
                match_with_bb_odd_id_list: JSON.stringify(successList),
                match_count: successList.length,
            });
        } catch (e) {
            (e as Error).message += 'Resulted in not calling "MEBB_Betslip_Event_Add_Result"';
            logger.error(e as Error);
        }
    };

    return {
        bannerDisplayed,
        bannerClicked,
        screenLoaded,
        screenBackClicked,
        eventCardHeaderClicked,
        marketCategoryClicked,
        marketFilterClicked,
        legAdded,
        legRemoved,
        summaryInteraction,
        summaryEventHeaderClicked,
        summaryCleared,
        summaryUnavailableRemoved,
        summarySubmitClicked,
        summarySubmitResult,
    };
};
